import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Collapse, Typography } from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  StyledMenuItemContainer,
  StyledMenuListItemText,
  StyledSubMenuItemContainer,
} from "../StyledComponents/styled";
import { erase_duplicates } from "../../../api_calls/utils";
import {
  buildGroupedSelectorOptions,
  estimateOptionsContentWidth,
} from "./selectorOptionUtils";
import SelectFieldShell from "./SelectFieldShell";
import useSelectDropdown from "./useSelectDropdown";
import {
  SELECTED_OPTION_COLOR,
  SELECT_COLOR,
  SELECT_HIGHLIGHT_COLOR,
  SELECT_MOTION_MS,
} from "./selectConstants";

// Child rows sit one indent level in (StyledSubMenuItemContainer).
const GROUP_OPTION_INDENT_PX = 25;

export default function GroupedSingleSelect({
  label,
  defaultVal,
  elemId,
  options,
  onChangeCall,
  fieldError,
  enableFullWidth,
  selectionCheck,
  readOnlyFlag,
  groupParam,
  disableSearch,
  allowClearance,
  parentSelectable,
  getParentOption,
}) {
  const optionRefs = useRef({});
  const [selectedValue, setSelectedValue] = useState(defaultVal);
  const [expanded, setExpanded] = useState({});
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [instanceId] = useState(
    () => elemId || `grouped-single-select-${Math.random().toString(36).substr(2, 9)}`
  );

  const isDisabled = typeof readOnlyFlag !== "undefined" ? readOnlyFlag : false;
  const showSearch = typeof disableSearch !== "undefined" ? !disableSearch : true;
  const canClear = typeof allowClearance !== "undefined" ? allowClearance : false;
  const canSelectParent = parentSelectable === true;
  const handleDropdownClosed = useCallback(() => {
    setHighlightedIndex(-1);
  }, []);

  const dropdown = useSelectDropdown({
    instanceId,
    showSearch,
    onClosed: handleDropdownClosed,
  });
  const {
    open,
    setOpen,
    searchTerm,
    isFocused,
    close: closeDropdown,
  } = dropdown;

  // Keep the wrapper's historical close signature so selection and keyboard
  // handlers remain easy to compare with the pre-refactor implementation.
  const close = useCallback(
    (shouldRefocus = false) =>
      closeDropdown({ clearSearch: true, refocus: shouldRefocus }),
    [closeDropdown]
  );

  const shrink =
    isFocused ||
    open ||
    (selectedValue !== null &&
      selectedValue !== "" &&
      typeof selectedValue !== "undefined" &&
      Object.keys(selectedValue).length !== 0);

  useEffect(() => {
    setSelectedValue(defaultVal);
  }, [defaultVal]);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [searchTerm]);

  const uniqueOptions = useMemo(() => erase_duplicates(options), [options]);

  const groupedCategories = useMemo(
    () => buildGroupedSelectorOptions({ options: uniqueOptions, groupParam, getParentOption }),
    [uniqueOptions, groupParam, getParentOption]
  );

  const searchValue = searchTerm.trim().toLowerCase();

  const filteredCategories = useMemo(() => {
    if (!showSearch || !searchValue) return groupedCategories;

    return groupedCategories
      .map((cat) => {
        const lowerCaseGroupName = cat.groupName.toLowerCase();

        if (lowerCaseGroupName.includes(searchValue)) return cat;

        const filteredOptions = cat.options.filter((opt) => {
          const optLabel = String(opt?.label ?? "").toLowerCase();
          return optLabel.includes(searchValue);
        });

        if (filteredOptions.length > 0) return { ...cat, options: filteredOptions };

        return null;
      })
      .filter((cat) => cat !== null);
  }, [groupedCategories, searchValue, showSearch]);

  const isSelected = useCallback(
    (option) =>
      selectedValue !== null &&
      typeof selectedValue !== "undefined" &&
      Object.keys(selectedValue).length !== 0 &&
      selectionCheck(option, selectedValue),
    [selectedValue, selectionCheck]
  );

  // GroupedSingle closes after a selection; GroupedMulti stays open.
  const handleSelect = useCallback(
    (option) => {
      if (isDisabled) return;

      setSelectedValue(option);
      onChangeCall(option);
      setHighlightedIndex(-1);
      close();
    },
    [isDisabled, onChangeCall, close]
  );

  const handleGroupClick = useCallback(
    (category) => {
      if (canSelectParent) {
        handleSelect(category.parentOption);
        return;
      }

      setExpanded((prev) => ({ ...prev, [category.groupName]: !prev[category.groupName] }));
      setHighlightedIndex(-1);
    },
    [canSelectParent, handleSelect]
  );

  const handleGroupToggle = useCallback((groupName) => {
    setExpanded((prev) => ({ ...prev, [groupName]: !prev[groupName] }));
    setHighlightedIndex(-1);
  }, []);

  const handleClear = (e) => {
    e.stopPropagation();
    if (isDisabled) return;

    setSelectedValue(null);
    onChangeCall(null);
  };

  const flatOptionsList = useMemo(() => {
    const list = [];

    filteredCategories.forEach((cat) => {
      list.push({ type: "group", data: cat });

      if (expanded[cat.groupName]) {
        cat.options.forEach((option) => {
          list.push({ type: "option", option });
        });
      }
    });

    return list;
  }, [filteredCategories, expanded]);

  const scrollHighlightedIntoView = useCallback((index) => {
    const element = optionRefs.current[`item-${index}`];
    if (element) element.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, []);

  const handleListKeyDown = useCallback(
    (e) => {
      if (!open) return;

      const totalCount = flatOptionsList.length;
      if (totalCount === 0 && e.key !== "Escape") return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          e.stopPropagation();
          setHighlightedIndex((prev) => {
            const next = prev < totalCount - 1 ? prev + 1 : 0;
            setTimeout(() => scrollHighlightedIntoView(next), 0);
            return next;
          });
          break;

        case "ArrowUp":
          e.preventDefault();
          e.stopPropagation();
          setHighlightedIndex((prev) => {
            const next = prev > 0 ? prev - 1 : totalCount - 1;
            setTimeout(() => scrollHighlightedIntoView(next), 0);
            return next;
          });
          break;

        case "Enter":
          e.preventDefault();
          e.stopPropagation();
          if (highlightedIndex >= 0 && highlightedIndex < flatOptionsList.length) {
            const item = flatOptionsList[highlightedIndex];
            if (item.type === "group") handleGroupClick(item.data);
            if (item.type === "option") handleSelect(item.option);
          }
          break;

        case "Escape":
          e.preventDefault();
          e.stopPropagation();
          close();
          break;

        default:
          break;
      }
    },
    [open, flatOptionsList, highlightedIndex, close, scrollHighlightedIntoView, handleSelect, handleGroupClick]
  );

  // Reported to the shell, which decides the final panel width. Group headers sit
  // flush left while their options are indented, so both are measured.
  const dropdownContentWidth = useMemo(() => {
    const groupHeaderWidth = estimateOptionsContentWidth(
      groupedCategories.map((category) => category.groupName)
    );
    const optionRowWidth = estimateOptionsContentWidth(
      uniqueOptions.map((option) => option?.label),
      GROUP_OPTION_INDENT_PX
    );

    return Math.max(groupHeaderWidth, optionRowWidth);
  }, [groupedCategories, uniqueOptions]);

  let flatIndex = -1;

  return (
    <SelectFieldShell
      dropdown={dropdown}
      elemId={elemId}
      label={label}
      shrink={shrink}
      disabled={isDisabled}
      enableFullWidth={enableFullWidth}
      canClear={canClear && Boolean(selectedValue)}
      onClear={handleClear}
      fieldError={fieldError}
      showSearch={showSearch}
      onAnchorClick={() => {
        if (!isDisabled) setOpen((previous) => !previous);
      }}
      onAnchorKeyDown={(event) => {
        if (isDisabled) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          setOpen(true);
        }
        if (event.key === "Escape") close();
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
          event.preventDefault();
          if (!open) setOpen(true);
        }
      }}
      onListKeyDown={handleListKeyDown}
      disablePortal={false}
      contentWidth={dropdownContentWidth}
      valueDisplay={
        <Typography
          variant="body2"
          sx={{
            color: isDisabled ? "#62626E" : SELECT_COLOR,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {selectedValue ? selectedValue.label : ""}
        </Typography>
      }
    >
      {filteredCategories.map((cat) => {
        const isExpanded = !!expanded[cat.groupName];
        const parentSelected = canSelectParent && isSelected(cat.parentOption);

        flatIndex++;
        const groupIndex = flatIndex;

        return (
          <Box key={cat.groupName} sx={{ width: "100%" }} onClick={(e) => e.stopPropagation()}>
            <StyledMenuItemContainer
              ref={(el) => {
                optionRefs.current[`item-${groupIndex}`] = el;
              }}
              onClick={() => handleGroupClick(cat)}
              onMouseEnter={() => setHighlightedIndex(groupIndex)}
              sx={{
                display: "flex",
                alignItems: "center",
                backgroundColor:
                  highlightedIndex === groupIndex
                    ? SELECT_HIGHLIGHT_COLOR
                    : parentSelected
                      ? SELECTED_OPTION_COLOR
                      : "transparent",
              }}
            >
              <StyledMenuListItemText
                primary={cat.groupName}
                primaryTypographyProps={{
                  sx: {
                    color: parentSelected ? SELECT_COLOR : "inherit",
                    fontWeight: parentSelected ? 500 : 400,
                  },
                }}
              />
              <Box
                component="span"
                onClick={(e) => {
                  // Parent rows can be selectable in future usages, so the
                  // expand icon keeps child visibility separate from selection.
                  e.stopPropagation();
                  handleGroupToggle(cat.groupName);
                }}
                sx={{ display: "flex", alignItems: "center", ml: 1 }}
              >
                {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </Box>
            </StyledMenuItemContainer>

            {/* Same group timing as the other grouped dropdowns. Keyboard indexes stay limited to open groups. */}
            <Collapse in={isExpanded} timeout={SELECT_MOTION_MS} unmountOnExit>
              {cat.options.map((option, index) => {
                const optionKey = option?.label ?? index;
                const checked = isSelected(option);
                const optionIndex = isExpanded ? ++flatIndex : -1;

                return (
                  <StyledSubMenuItemContainer
                    key={optionKey}
                    ref={(el) => {
                      if (optionIndex >= 0) optionRefs.current[`item-${optionIndex}`] = el;
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelect(option);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onMouseEnter={() => {
                      if (optionIndex >= 0) setHighlightedIndex(optionIndex);
                    }}
                    sx={{
                      "&.MuiMenuItem-root": {
                        paddingLeft: "20px",
                      },
                      display: "flex",
                      alignItems: "center",
                      backgroundColor:
                        highlightedIndex === optionIndex
                          ? SELECT_HIGHLIGHT_COLOR
                          : checked
                            ? SELECTED_OPTION_COLOR
                            : "transparent",
                    }}
                  >
                    <StyledMenuListItemText
                      primary={option.label}
                      primaryTypographyProps={{
                        sx: {
                          color: checked ? SELECT_COLOR : "inherit",
                          fontWeight: checked ? 500 : 400,
                        },
                      }}
                    />
                  </StyledSubMenuItemContainer>
                );
              })}
            </Collapse>
          </Box>
        );
      })}

      {filteredCategories.length === 0 && (
        <Box sx={{ p: 2, textAlign: "center" }}>
          <Typography variant="body2" color="textSecondary">
            No options found
          </Typography>
        </Box>
      )}
    </SelectFieldShell>
  );
}
